import { useConsole } from "contexts/Console"
import styled from "@emotion/styled"
import { fullWidth } from "css/box"
import { useLayoutEffect, useRef } from "react"
import { motion, useMotionValue, transform } from "framer-motion"
import Variations from "components/family-variations/Variations"
import { useSuspender } from "contexts/Suspender"
import { useLocale } from "contexts/Locale"
import { fullGrid } from "css/grid"
import * as text from "css/text"
import getMediaQuery from "css/breakpoints"
import { useEnv } from "contexts/Env"

const Root = styled(motion.section)`
  ${fullWidth};
  background: var(--gradient);
`

const Head = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 3;
  /* height: 50vh; */
  height: auto;

  ${fullGrid}
  grid-template-rows: 1fr [content] auto 1fr;
  pointer-events: none;
  user-select: none;

  & > p {
    grid-column: col 1 / span 6;
    grid-row: content;
    margin-block-start: 15vh;
    ${getMediaQuery("m")} {
      grid-column: col 3 / span 8;
    }

    ${getMediaQuery("l")} {
      grid-column: col 5 / span 4;
    }

    color: white;
    background: linear-gradient(to right, var(--tgs), var(--tge) 33%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    ${text.headline50}/* width: calc(var(--grid-col-unit) * 6 + var(--grid-gap) * 5); */

    /* outline: 1px solid magenta; */
  }

  opacity: calc(1 - var(--t, 0));
  transition: opacity calc(var(--fx, 300) * 1ms);
  will-change: opacity;
`

export default function FamilyVariations({
  background_gradient,
  title_gradient,
  heading,
  variations: propVariations = [],
  start = 1,
  frames = 250,
  theme = "light-theme",
  className,
  ...props
}) {
  const console = useConsole()
  const rvariations = useRef()
  const rhead = useRef()
  const locale = useLocale()
  const env = useEnv()

  const [titleGradientStart, titleGradientEnd] = new Array(2).fill(null).map((_, i) => {
    let color, opacity

    while (true) {
      if (title_gradient?.colors?.[i]) {
        color = title_gradient.colors[i].color
        opacity = title_gradient.colors[i].opacity
        break
      } else if (i > 0) {
        i -= 1
        continue
      } else {
        color = "33 33 33"
        opacity = 0
        break
      }
    }

    return `rgb(${color} / ${opacity})`
  })

  const [gradientStartColor, gradientMidColor, gradientEndColor] = new Array(3).fill(null).map((_, i) => {
    let color, opacity

    while (true) {
      if (background_gradient?.colors?.[i]) {
        color = background_gradient.colors[i].color
        opacity = background_gradient.colors[i].opacity
        break
      } else if (i > 0) {
        i -= 1
        continue
      } else {
        color = "33 33 33"
        opacity = 0
        break
      }
    }

    return `rgb(${color} / ${opacity})`
  })

  const [[err, variations], variationsSSR] = useSuspender(
    ["famvar", locale.current.codes.www, ...propVariations.map(({ rmc }) => rmc).sort()],
    () =>
      Promise.resolve(propVariations.map(({ rmc }) => rmc))
        .then(rmcs => {
          const url = new URL("/api/catalog/watches", process.browser ? env.services : process.env.SERVICE_CATALOG_URL)
          url.searchParams.append("language", locale.current.codes.www)
          url.searchParams.append("rmc", `(${rmcs.join(",")})`)

          return url
        })
        .then(url => new Request(url))
        .then(request => fetch(request))
        .then(response => response.json())
        .then(({ results }) => {
          for (const result of results) {
            const variation = propVariations.find(({ rmc }) => rmc === result.rmc)
            Object.assign(variation, result)
          }
          return propVariations
        })
        .then(data => [null, data])
        .catch(err => [err, null]),
    true
  )

  if (err) console.error(err)

  const t = useMotionValue(0)

  useLayoutEffect(() => {
    if (err) return

    const variations = rvariations.current
    const transformer = transform([0.45, 0.8], [0, 1])
    let lastHRT = performance.now()
    const observer = new IntersectionObserver(
      ([intersection]) => {
        const delta = intersection.time - lastHRT
        lastHRT = intersection.time
        rhead.current?.style?.setProperty?.("--fx", delta > 150 ? 300 : 0)
        const next =
          intersection.isIntersecting && intersection.boundingClientRect.y <= 0
            ? 1
            : !intersection.isIntersecting
            ? intersection.boundingClientRect.y < 0
              ? 1
              : 0
            : transformer(intersection.intersectionRatio)
        t.set(next)
      },
      {
        threshold: [0.0, 0.1, 0.2, 0.3, 0.4, 0.45, 0.5, 0.6, 0.65, 0.7, 0.72, 0.74, 0.76, 0.78, 0.8, 0.9, 1.0],
      }
    )

    observer.observe(variations)
    return () => {
      observer.unobserve(variations)
      observer.disconnect()
    }
  })

  console.verbose("FamilyVariations(%o)", {
    backgroundGradient: background_gradient,
    start,
    frames,
    variations: [propVariations, variations],
    className,
    theme,
    props,
  })

  return err ? null : (
    <>
      {variationsSSR}
      <Root
        className={`${className}`}
        // heading={heading}
        style={{
          "--t": t,
          "--gsc": gradientStartColor,
          "--gmc": gradientMidColor,
          "--gec": gradientEndColor,
          "--angle": `${background_gradient?.angle ?? "180"}deg`,
          "--gradient": heading
            ? `linear-gradient(var(--angle), var(--gsc) 33%, var(--gmc), var(--gec))`
            : `linear-gradient(var(--angle), var(--gsc), var(--gec))`,
        }}
      >
        {heading && (
          <Head ref={rhead}>
            <p style={{ "--tgs": titleGradientStart, "--tge": titleGradientEnd }}>{heading}</p>
          </Head>
        )}
        <Variations theme={theme} className={className} ref={rvariations} variations={variations} start={start} frames={frames} heading={heading} />
      </Root>
    </>
  )
}
